import { useState } from 'react';
import {
  DOCUMENT_TYPE_LABELS,
  TEMPLATE_VERSION_STATUS_LABELS,
  type DocumentType,
  type TemplateDto,
} from '../../lib/api-types';
import type { CalcMethodCode } from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { useAuth } from '../../auth/AuthContext';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import {
  useActivateTemplateVersion,
  useCreateTemplate,
  useCreateTemplateVersion,
  useTemplates,
} from './hooks';

const DOCUMENT_TYPES: DocumentType[] = [
  'PV_BATI',
  'PV_EXPERTISE_COMPLETE',
  'RAPPORT_EXPERTISE',
  'DOCUMENT_METHODE_SPECIFIQUE',
];

const METHODS: CalcMethodCode[] = ['COUT', 'COMPARAISON', 'CAPITALISATION', 'MOYENNE'];

/**
 * Role → capability mapping, mirrored from the API's ROLE_PERMISSIONS (server remains
 * authoritative — this only hides controls the user cannot use). Manage = author templates &
 * versions; Activate = four-eyes activation.
 */
function useTemplateCapabilities(): { canManage: boolean; canActivate: boolean } {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  return {
    canManage: roles.includes('ADMIN_SYSTEME') || roles.includes('ADMIN_METIER'),
    canActivate: roles.includes('ADMIN_SYSTEME') || roles.includes('VALIDATEUR_REFERENTIEL'),
  };
}

/** Read a File as base64 (without the data: prefix) — the server hashes & stores the bytes. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Lecture du fichier impossible'));
    reader.readAsDataURL(file);
  });
}

/**
 * « Modèles de documents » — écran d'administration (LOT 7A). Liste les modèles versionnés et leurs
 * statuts ; création d'un modèle + version (DOCX en base64) et activation (four-eyes). Les contrôles
 * de gestion/activation sont masqués si l'utilisateur n'a pas la permission — le serveur applique la
 * règle dans tous les cas.
 */
export function TemplatesTab(): JSX.Element {
  const templates = useTemplates();
  const createTemplate = useCreateTemplate();
  const createVersion = useCreateTemplateVersion();
  const activate = useActivateTemplateVersion();
  const { canManage, canActivate } = useTemplateCapabilities();

  // Create-template form.
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('PV_BATI');
  const [description, setDescription] = useState('');

  // Add-version form (scoped to a template).
  const [versionTemplateId, setVersionTemplateId] = useState<string | null>(null);
  const [docxBase64, setDocxBase64] = useState('');
  const [docxFilename, setDocxFilename] = useState('');
  const [configuredMethod, setConfiguredMethod] = useState<CalcMethodCode | ''>('');
  const [fileError, setFileError] = useState<string | null>(null);

  const canCreateTemplate =
    canManage && code.trim() !== '' && name.trim() !== '' && !createTemplate.isPending;

  const runCreateTemplate = (): void => {
    if (!canCreateTemplate) return;
    createTemplate.mutate(
      {
        code: code.trim(),
        name: name.trim(),
        documentType,
        ...(description.trim() !== '' ? { description: description.trim() } : {}),
      },
      {
        onSuccess: () => {
          setCode('');
          setName('');
          setDescription('');
        },
      },
    );
  };

  const onPickFile = async (file: File | undefined): Promise<void> => {
    setFileError(null);
    if (!file) {
      setDocxBase64('');
      setDocxFilename('');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setDocxBase64(base64);
      setDocxFilename(file.name);
    } catch (err) {
      setFileError(toDisplayError(err).message);
    }
  };

  const runCreateVersion = (templateId: string): void => {
    if (docxBase64 === '') return;
    createVersion.mutate(
      {
        id: templateId,
        input: {
          docxBase64,
          ...(configuredMethod !== '' ? { configuredMethod } : {}),
        },
      },
      {
        onSuccess: () => {
          setVersionTemplateId(null);
          setDocxBase64('');
          setDocxFilename('');
          setConfiguredMethod('');
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      {/* Create a template (author only). */}
      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle>Nouveau modèle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="tpl-code">Code</Label>
                <Input id="tpl-code" value={code} onChange={(e) => setCode(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="tpl-name">Nom</Label>
                <Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="tpl-type">Type de document</Label>
                <Select
                  id="tpl-type"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value as DocumentType)}
                >
                  {DOCUMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {DOCUMENT_TYPE_LABELS[t]}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="tpl-desc">Description (facultatif)</Label>
                <Input
                  id="tpl-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
            <Button type="button" onClick={runCreateTemplate} disabled={!canCreateTemplate}>
              {createTemplate.isPending ? 'Création…' : 'Créer le modèle'}
            </Button>
            {createTemplate.isError ? (
              <Alert variant="error" details={toDisplayError(createTemplate.error).details}>
                {toDisplayError(createTemplate.error).message}
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Templates list + versions. */}
      <Card>
        <CardHeader>
          <CardTitle>Modèles de documents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {templates.isLoading ? (
            <p className="text-sm text-slate-500">Chargement…</p>
          ) : templates.isError ? (
            <Alert variant="error">{toDisplayError(templates.error).message}</Alert>
          ) : templates.data && templates.data.length > 0 ? (
            templates.data.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                canManage={canManage}
                canActivate={canActivate}
                isAddingVersion={versionTemplateId === tpl.id}
                onToggleAddVersion={() =>
                  setVersionTemplateId((cur) => (cur === tpl.id ? null : tpl.id))
                }
                docxFilename={docxFilename}
                configuredMethod={configuredMethod}
                onPickFile={onPickFile}
                onChangeMethod={setConfiguredMethod}
                onSubmitVersion={() => runCreateVersion(tpl.id)}
                versionPending={createVersion.isPending}
                versionError={
                  versionTemplateId === tpl.id && createVersion.isError
                    ? toDisplayError(createVersion.error).message
                    : null
                }
                fileError={versionTemplateId === tpl.id ? fileError : null}
                docxReady={docxBase64 !== ''}
                onActivate={(versionId) => activate.mutate(versionId)}
                activatePending={activate.isPending}
              />
            ))
          ) : (
            <p className="text-sm text-slate-500">Aucun modèle enregistré.</p>
          )}
          {activate.isError ? (
            <Alert variant="error" details={toDisplayError(activate.error).details}>
              {toDisplayError(activate.error).message}
            </Alert>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

interface TemplateCardProps {
  template: TemplateDto;
  canManage: boolean;
  canActivate: boolean;
  isAddingVersion: boolean;
  onToggleAddVersion: () => void;
  docxFilename: string;
  configuredMethod: CalcMethodCode | '';
  onPickFile: (file: File | undefined) => void;
  onChangeMethod: (m: CalcMethodCode | '') => void;
  onSubmitVersion: () => void;
  versionPending: boolean;
  versionError: string | null;
  fileError: string | null;
  docxReady: boolean;
  onActivate: (versionId: string) => void;
  activatePending: boolean;
}

function TemplateCard(props: TemplateCardProps): JSX.Element {
  const { template: tpl } = props;
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold text-slate-800">{tpl.code}</span>
        <span className="text-sm text-slate-700">{tpl.name}</span>
        <Badge tone="info">{DOCUMENT_TYPE_LABELS[tpl.documentType]}</Badge>
        {props.canManage ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={props.onToggleAddVersion}
          >
            {props.isAddingVersion ? 'Annuler' : 'Ajouter une version'}
          </Button>
        ) : null}
      </div>
      {tpl.description ? <p className="text-sm text-slate-500">{tpl.description}</p> : null}

      {/* Versions table. */}
      {tpl.versions.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3">Version</th>
                <th className="py-2 pr-3">Statut</th>
                <th className="py-2 pr-3">SHA-256</th>
                <th className="py-2 pr-3">Méthode</th>
                <th className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {tpl.versions.map((v) => (
                <tr key={v.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-medium text-slate-800">v{v.version}</td>
                  <td className="py-2 pr-3">
                    <Badge tone={v.status === 'ACTIF' ? 'success' : 'neutral'}>
                      {TEMPLATE_VERSION_STATUS_LABELS[v.status]}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-slate-500">
                    {v.docxSha256.slice(0, 12)}…
                  </td>
                  <td className="py-2 pr-3">{v.configuredMethod ?? '—'}</td>
                  <td className="py-2 pr-3 text-right">
                    {props.canActivate && v.status !== 'ACTIF' ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => props.onActivate(v.id)}
                        disabled={props.activatePending}
                      >
                        Activer
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-500">Aucune version.</p>
      )}

      {/* Add-version form. */}
      {props.isAddingVersion ? (
        <fieldset className="space-y-2 rounded-md border border-slate-200 p-3">
          <legend className="px-1 text-xs uppercase tracking-wide text-slate-400">
            Nouvelle version
          </legend>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label htmlFor={`docx-${tpl.id}`}>Fichier .docx</Label>
              <input
                id={`docx-${tpl.id}`}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                aria-label="Fichier .docx"
                onChange={(e) => props.onPickFile(e.target.files?.[0])}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm"
              />
              {props.docxFilename ? (
                <p className="mt-1 text-xs text-slate-500">{props.docxFilename}</p>
              ) : null}
            </div>
            <div>
              <Label htmlFor={`method-${tpl.id}`}>Méthode configurée (facultatif)</Label>
              <Select
                id={`method-${tpl.id}`}
                value={props.configuredMethod}
                onChange={(e) => props.onChangeMethod(e.target.value as CalcMethodCode | '')}
              >
                <option value="">— Aucune —</option>
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          {props.fileError ? <Alert variant="error">{props.fileError}</Alert> : null}
          <Button
            type="button"
            onClick={props.onSubmitVersion}
            disabled={!props.docxReady || props.versionPending}
          >
            {props.versionPending ? 'Envoi…' : 'Créer la version'}
          </Button>
          {props.versionError ? <Alert variant="error">{props.versionError}</Alert> : null}
        </fieldset>
      ) : null}
    </div>
  );
}
